import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  Package, FileText, Clock, CheckCircle, Truck, 
  LogOut, ChevronRight, Loader2, CreditCard,
  Calendar, DollarSign, Eye
} from 'lucide-react';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';

const CustomerPortal = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const customerName = localStorage.getItem('customerName') || 'Customer';

  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    if (!token) {
      navigate('/portal/login');
      return;
    }
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      const response = await axios.get('/customer/dashboard', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerEmail');
        localStorage.removeItem('customerName');
        navigate('/portal/login');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('customerToken');
    localStorage.removeItem('customerEmail');
    localStorage.removeItem('customerName');
    navigate('/portal/login');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      'draft': '#6b7280',
      'sent': '#3b82f6',
      'paid': '#10b981',
      'ordered': '#8b5cf6',
      'Pending': '#f59e0b',
      'Ordered': '#3b82f6',
      'In Production': '#8b5cf6',
      'Shipped': '#10b981',
      'Delivered': '#059669'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': Clock,
      'Ordered': FileText,
      'In Production': Package,
      'Shipped': Truck,
      'Delivered': CheckCircle
    };
    return icons[status] || Clock;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Loader2 size={40} style={{ color: '#e74c3c', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '16px 24px',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              background: '#e74c3c',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <span style={{ color: 'white', fontWeight: 'bold', fontSize: '18px' }}>S</span>
            </div>
            <div>
              <h1 style={{ color: 'white', margin: 0, fontSize: '18px', fontWeight: '600' }}>
                Customer Portal
              </h1>
              <p style={{ color: '#9ca3af', margin: 0, fontSize: '12px' }}>
                Welcome, {customerName}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            style={{ color: 'white' }}
            data-testid="logout-button"
          >
            <LogOut size={18} style={{ marginRight: '8px' }} />
            Logout
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main style={{ maxWidth: '1200px', margin: '0 auto', padding: '24px' }}>
        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '32px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '44px',
                height: '44px',
                background: '#dbeafe',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FileText size={22} color="#3b82f6" />
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>Total Proposals</p>
                <p style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a2e', margin: 0 }}>
                  {data?.stats?.total_proposals || 0}
                </p>
              </div>
            </div>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '44px',
                height: '44px',
                background: '#dcfce7',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CheckCircle size={22} color="#10b981" />
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>Paid</p>
                <p style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a2e', margin: 0 }}>
                  {data?.stats?.paid_proposals || 0}
                </p>
              </div>
            </div>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '44px',
                height: '44px',
                background: '#fef3c7',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Clock size={22} color="#f59e0b" />
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>Pending</p>
                <p style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a2e', margin: 0 }}>
                  {data?.stats?.pending_proposals || 0}
                </p>
              </div>
            </div>
          </div>

          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{
                width: '44px',
                height: '44px',
                background: '#ede9fe',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <Package size={22} color="#8b5cf6" />
              </div>
              <div>
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>Active Orders</p>
                <p style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a2e', margin: 0 }}>
                  {data?.stats?.active_orders || 0}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick-action banner — primary call to action for returning customers
            who want to place a new order without waiting for the admin. */}
        <div
          data-testid="new-order-cta"
          onClick={() => navigate('/portal/new')}
          style={{
            background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
            color: '#fff', borderRadius: 12, padding: '20px 22px', marginBottom: 24,
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxShadow: '0 6px 18px rgba(231, 76, 60, 0.25)',
          }}
        >
          <div>
            <div style={{ fontSize: 13, opacity: 0.9, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Start something new
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 4 }}>
              Place a new order
            </div>
            <div style={{ fontSize: 13, opacity: 0.9, marginTop: 2 }}>
              Send us your project brief + artwork — we&apos;ll reply with a custom proposal.
            </div>
          </div>
          <div style={{ background: 'rgba(255,255,255,0.18)', borderRadius: 999, padding: '10px 16px', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>
            New Order →
          </div>
        </div>

        {/* Account-management strip — set/reset password without leaving the portal */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="set-password-link"
            onClick={async () => {
              const email = localStorage.getItem('customerEmail');
              if (!email) return toast.error('Please re-login');
              try {
                await axios.post('/customer/request-password-setup', { email });
                toast.success('Password setup link emailed to you.');
              } catch (err) {
                toast.error(err.response?.data?.detail || 'Could not send setup email');
              }
            }}
            style={{
              padding: '8px 14px', background: '#fff', border: '1px solid #cbd5e1',
              borderRadius: 999, fontSize: 12, color: '#475569', cursor: 'pointer', fontWeight: 600,
            }}
          >
            🔑 Set / reset password
          </button>
        </div>
        {/* Active Orders Section */}
        {data?.orders?.length > 0 && (
          <section style={{ marginBottom: '32px' }}>
            <h2 style={{ 
              fontSize: '18px', 
              fontWeight: '600', 
              color: '#1a1a2e',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Package size={20} />
              Your Orders
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {data.orders.map(order => {
                const StatusIcon = getStatusIcon(order.status);
                return (
                  <Link
                    key={order.id}
                    to={`/portal/order/${order.id}`}
                    style={{ textDecoration: 'none' }}
                    data-testid={`order-card-${order.id}`}
                  >
                    <div style={{
                      background: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'box-shadow 0.2s, transform 0.2s',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <div style={{
                          width: '50px',
                          height: '50px',
                          background: `${getStatusColor(order.status)}15`,
                          borderRadius: '12px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <StatusIcon size={24} color={getStatusColor(order.status)} />
                        </div>
                        <div>
                          <p style={{ 
                            fontWeight: '600', 
                            color: '#1a1a2e', 
                            margin: '0 0 4px',
                            fontSize: '15px'
                          }}>
                            {order.po_number}
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                            <span style={{ 
                              fontSize: '13px', 
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <Calendar size={14} />
                              {formatDate(order.created_at)}
                            </span>
                            <span style={{ 
                              fontSize: '13px', 
                              color: '#6b7280',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <DollarSign size={14} />
                              {formatCurrency(order.total_amount)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span style={{
                          background: `${getStatusColor(order.status)}15`,
                          color: getStatusColor(order.status),
                          padding: '6px 14px',
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: '600'
                        }}>
                          {order.status}
                        </span>
                        <ChevronRight size={20} color="#9ca3af" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Proposals Section */}
        <section>
          <h2 style={{ 
            fontSize: '18px', 
            fontWeight: '600', 
            color: '#1a1a2e',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <FileText size={20} />
            Your Proposals
          </h2>
          
          {data?.proposals?.length === 0 ? (
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '40px',
              textAlign: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <FileText size={48} color="#d1d5db" style={{ marginBottom: '16px' }} />
              <p style={{ color: '#6b7280', margin: 0 }}>No proposals yet</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {data?.proposals?.map(proposal => (
                <div
                  key={proposal.id}
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                  data-testid={`proposal-card-${proposal.id}`}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      background: '#f3f4f6',
                      borderRadius: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FileText size={24} color="#6b7280" />
                    </div>
                    <div>
                      <p style={{ 
                        fontWeight: '600', 
                        color: '#1a1a2e', 
                        margin: '0 0 4px',
                        fontSize: '15px'
                      }}>
                        {proposal.proposal_number}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{ 
                          fontSize: '13px', 
                          color: '#6b7280',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <Calendar size={14} />
                          {formatDate(proposal.created_at)}
                        </span>
                        <span style={{ 
                          fontSize: '13px', 
                          color: '#6b7280',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}>
                          <Package size={14} />
                          {proposal.quantity} items
                        </span>
                        <span style={{ 
                          fontSize: '13px', 
                          fontWeight: '600',
                          color: '#1a1a2e'
                        }}>
                          {formatCurrency(proposal.total_amount)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <span style={{
                      background: `${getStatusColor(proposal.status)}15`,
                      color: getStatusColor(proposal.status),
                      padding: '6px 14px',
                      borderRadius: '20px',
                      fontSize: '13px',
                      fontWeight: '600',
                      textTransform: 'capitalize'
                    }}>
                      {proposal.status}
                    </span>
                    {proposal.status === 'sent' && (
                      <Link to={`/pay/${proposal.id}?t=${proposal.share_token || ''}`}>
                        <Button
                          size="sm"
                          style={{
                            background: '#10b981',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          <CreditCard size={16} />
                          Pay Now
                        </Button>
                      </Link>
                    )}
                    <Link to={`/portal/proposal/${proposal.id}`}>
                      <Button variant="ghost" size="sm">
                        <Eye size={16} style={{ marginRight: '6px' }} />
                        View
                      </Button>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>

      {/* Footer */}
      <footer style={{
        background: '#1a1a2e',
        padding: '24px',
        marginTop: '40px',
        textAlign: 'center'
      }}>
        <p style={{ color: '#6b7280', margin: 0, fontSize: '13px' }}>
          © {new Date().getFullYear()} {brandName()} | Custom Apparel & Print Solutions
        </p>
        <p style={{ color: '#4b5563', margin: '8px 0 0', fontSize: '12px' }}>
          Questions? Contact us at {brandEmail()}
        </p>
      </footer>
    </div>
  );
};

export default CustomerPortal;
